/* eslint-disable react-hooks/exhaustive-deps */
import React, {useCallback, useMemo, useState} from 'react';
import styled from 'styled-components';
import {useFrameService} from '@ey/canvascoreservices';
import Utility from '../../../../../../../../util/utilityfunctions';
import Env from '../../../../../../../../util/env';
import {labels} from '../../../../../../../../util/utils';
import Tooltip from '../../../../../../../Common/BaseComponentWrappers/MotifTooltip';
import EllipseControl from '../../../../../../../Common/EllipsesControl/EllipsesControl';
import {useSelector} from 'react-redux';
import {isEmpty, orderBy} from 'lodash';
import {useUserPreferences} from '../../../../../../../../util/customHooks';
import {EngagementCommentStatus} from '../../../../../../../../util/uiconstants';

function CompareTooltip({data, commentType, value, renderer}) {
	const frameService = useFrameService();

	const documents = useSelector((store) => store.documents);

	const userPreferences = useUserPreferences();

	const preferences = useMemo(() => {
		return JSON.parse(userPreferences.userPreferencesJson);
	}, [userPreferences]);

	const getFilteredDocumentIds = (documents, predicate) => documents.filter(predicate).map((doc) => doc.documentId);

	const associatedDocumentIds = useMemo(() => {
		if (!data?.associatedDocuments || data.associatedDocuments.length === 0) {
			return [];
		}

		//Open comments documents
		if (commentType === EngagementCommentStatus.open) {
			return getFilteredDocumentIds(data.associatedDocuments, (doc) => doc.hasOpenComments);
		}

		//Cleared comments documents
		if (commentType === EngagementCommentStatus.cleared) {
			return getFilteredDocumentIds(data.associatedDocuments, (doc) => doc.hasClearedComments);
		}

		//Count of responses documents
		return getFilteredDocumentIds(data.associatedDocuments, (doc) => doc.documentId);
	}, [data?.associatedDocuments, commentType]);

	const compareDocuments = useMemo(() => {
		return documents?.allIds?.map((id) => documents.byId[id]) ?? [];
	}, [documents]);

	const findAresTaskId = useCallback((document) => {
		let aresTaskId;

		if (document && !isEmpty(document)) {
			aresTaskId = document.tasks?.find((task) => task.isSystemAssociated)?.id;
			if (!aresTaskId) {
				aresTaskId = document.tasks?.[0]?.id;
			}
		}

		return aresTaskId;
	}, []);

	const [forceHideCounter, setForceHideCounter] = useState(0);

	const linkItems = useMemo(() => {
		if (documents && documents?.allIds?.length > 0 && associatedDocumentIds.length > 0) {
			const docs = documents.allIds.filter((id) => associatedDocumentIds.includes(id)).map((id) => documents.byId[id]);

			return orderBy(docs, [(doc) => doc.documentName.toLowerCase()], ['asc']).map((doc) => {
				return {
					documentId: doc.id,
					documentName: `${doc.documentName}.${doc.fileExt}`,
					stEntityId: doc?.entities?.[0]?.id
				};
			});
		}

		return [];
	}, [associatedDocumentIds, documents, data?.associatedDocuments]);

	const handleClick = useCallback(
		(documentId) => {
			setForceHideCounter(forceHideCounter + 1);

			const document = data?.associatedDocuments?.find((doc) => doc.documentId === documentId);

			if (!document) {
				return;
			}

			const stEntityId = linkItems?.find((item) => item.documentId === documentId)?.stEntityId;

			const found = compareDocuments?.find((doc) => doc.id === documentId);

			const taskId = findAresTaskId(found);

			let kronosBaseUrl = Env.getURL('kronos_web');

			if (kronosBaseUrl && kronosBaseUrl.endsWith('/')) {
				kronosBaseUrl = kronosBaseUrl.slice(0, -1);
			}

			const uri = Utility.getAresTaskListNavigationUrl(
				document.documentId,
				document.formHeaderId,
				document.formSectionId,
				data.engagementId,
				taskId,
				{
					bodyId: document.formBodyId,
					isDrawer: true,
					isBodyDetailView: true,
					stEntityId
				},
				document.documentTypeId
			);

			frameService.openFrameFromChild({
				isAresView: true,
				variant: 'slideout',
				frameArgs: {
					name: `${labels.compareResponsesLabel}: ${Utility.getHtmlTextElement(data.sectionName)}`,
					src: kronosBaseUrl + uri,
					canOpenNewWindow: false,
					frameStore: {
						associatedDocumentIds
					}
				}
			});
		},
		[
			data.documentId,
			data.engagementId,
			data.formBodyId,
			data.headerId,
			data.sectionId,
			data.associatedDocuments,
			forceHideCounter,
			compareDocuments,
			findAresTaskId,
			associatedDocumentIds
		]
	);

	if (value) {
		return (
			<StyledCompareTooltip className="StyledCompareTooltip">
				{linkItems.length === 1 ? (
					<section onClick={() => handleClick(linkItems[0].documentId)}>{renderer}</section>
				) : (
					<Tooltip
						trigger={renderer}
						className={`compare-tooltip ${preferences?.theme}`}
						variant="alt"
						allowHover={false}
						closeButtonProps={{
							title: labels.close
						}}
						forceHideCounter={forceHideCounter}
					>
						<section className="tootip-content">
							<section className="header">
								<EllipseControl
									content={labels.associatedDocuments}
									tooltip={labels.associatedDocuments}
									noOfLines={1}
								/>
							</section>
							<section className="documents customScrollbar">
								{linkItems.map((doc, index) => {
									return (
										<section
											className={`link-item ${index + 1 === linkItems.length && 'last'}`}
											key={doc.documentId}
											onClick={() => handleClick(doc.documentId)}
										>
											<EllipseControl content={doc.documentName} tooltip={doc.documentName} noOfLines={1} />
										</section>
									);
								})}
							</section>
						</section>
					</Tooltip>
				)}
			</StyledCompareTooltip>
		);
	}

	return null;
}

export default CompareTooltip;

const StyledCompareTooltip = styled.section`
	display: flex;
	justify-content: center;

	.trigger-count {
		font-size: var(--px-12);
		font-weight: 700;
		line-height: var(--px-20);
		color: var(--blue-600);
		cursor: pointer;

		&:hover {
			text-decoration: underline;
		}
	}

	.motif-tooltip-wrapper {
		display: flex;
	}

	.trigger-link {
		display: flex;
		flex-direction: row;

		.link {
			display: flex;
			font-size: var(--px-12);
			font-weight: 700;
			line-height: var(--px-16);
			cursor: pointer;
			color: var(--blue-600);

			&:hover {
				text-decoration: underline;
			}
		}

		.link-label {
			display: flex;
			font-size: var(--px-12);
			font-weight: 300;
			line-height: var(--px-16);
			color: var(--neutrals-900);
			margin-left: var(--px-4);
		}
	}
`;
